/**
 * DownloadDocxButton — the AI Tools ".docx" download affordance.
 *
 * Replaces the bare `<a href={downloadUrl} download>` anchors (which dead-end
 * in iOS in-app browsers — see src/lib/downloadDocx.ts). Fetches the
 * authenticated binary and hands it to the OS share sheet (iOS) or a transient
 * objectURL download (desktop), surfacing a loading state while the server
 * renders the docx (~1-2s) and an in-app error if delivery fails — never the
 * silent blank screen of the anchor approach.
 */

import { useState, type ReactNode } from 'react';
import { downloadDocx, downloadDocxPost } from '@/lib/downloadDocx';
import { Button, type ButtonVariant, type ButtonSize } from './primitives/Button';
import { errorText, FALLBACK_SUPPORT_CONTACT } from '@/lib/errorCopy';

interface DownloadDocxButtonProps {
  /** Same-origin authenticated download URL (e.g. aiToolsApi.assessmentDownloadUrl). */
  url: string;
  /** Fallback filename used only when the response carries no Content-Disposition. */
  filename: string;
  className?: string;
  variant?: ButtonVariant;
  size?: ButtonSize;
  children?: ReactNode;
  /** Mirrors the old anchor's data-testid so existing queries keep working. */
  testId?: string;
  /**
   * When present, the download POSTs this body to `url` (the stateless
   * Medication List path) instead of GETting a saved draft by id. Absent = GET.
   */
  postBody?: unknown;
}

export function DownloadDocxButton({
  url,
  filename,
  className,
  variant = 'secondary',
  size = 'md',
  children = 'Download .docx',
  testId,
  postBody,
}: DownloadDocxButtonProps): ReactNode {
  const [status, setStatus] = useState<'idle' | 'preparing' | 'error'>('idle');

  async function handleClick(): Promise<void> {
    setStatus('preparing');
    try {
      if (postBody !== undefined) {
        await downloadDocxPost(url, postBody, filename);
      } else {
        await downloadDocx(url, filename);
      }
      setStatus('idle');
    } catch {
      setStatus('error');
    }
  }

  return (
    <span className="inline-flex max-w-full flex-col items-stretch gap-1">
      <Button
        type="button"
        onClick={handleClick}
        disabled={status === 'preparing'}
        aria-busy={status === 'preparing'}
        className={className}
        variant={variant}
        size={size}
        data-testid={testId}
      >
        {status === 'preparing' ? 'Preparing…' : children}
      </Button>
      {status === 'error' && (
        <span
          role="alert"
          className="text-xs text-warm-700"
          data-testid={testId ? `${testId}-error` : undefined}
        >
          {errorText('document_download', 'failed', FALLBACK_SUPPORT_CONTACT)}
        </span>
      )}
    </span>
  );
}
